import { fitsAudience, type Agenda, type AgendaActivity, type EligibilityItem } from '@koderek/shared';
import { ALL_DAY_MIN_MINUTES } from '../agenda/agendaModel';

const HOUR = 3_600_000;
/** Krótsze wolne okno nie jest pokazywane – to zwykle przejście między salami. */
export const MIN_GAP_MS = 15 * 60_000;

export interface Interval {
  start: number;
  end: number;
}

const startOf = (a: AgendaActivity) => new Date(a.startsAt).getTime();
const endOf = (a: AgendaActivity) => new Date(a.endsAt).getTime();

/**
 * Zajęcia trwające równolegle z innymi przez większość dnia (typ nieblokujący czasu, co najmniej dwie godziny – np. gra
 * terenowa, stoisko) nie trafiają na oś: zasłaniałyby tor i nie mówią nic o wolnym czasie. Ta sama granica co pasek
 * „Przez cały dzień” w agendzie.
 */
export function isBackground(activity: AgendaActivity, types: Agenda['types']): boolean {
  const type = types.find((ty) => ty.id === activity.typeId);
  return !!type && !type.blocksTime && (endOf(activity) - startOf(activity)) / 60_000 >= ALL_DAY_MIN_MINUTES;
}

/** Zakres osi: od pełnej godziny przed pierwszymi zajęciami (lub początkiem edycji) do pełnej godziny po ostatnich. */
export function dayRange(agenda: Agenda): Interval {
  const live = agenda.activities.filter((a) => a.status !== 'cancelled');
  const starts = [new Date(agenda.edition.startsAt).getTime(), ...live.map(startOf)];
  const ends = [new Date(agenda.edition.endsAt).getTime(), ...live.map(endOf)];
  // Strefa Europe/Warsaw ma przesunięcie o pełne godziny, więc pełna godzina UTC to pełna godzina lokalna.
  return { start: Math.floor(Math.min(...starts) / HOUR) * HOUR, end: Math.ceil(Math.max(...ends) / HOUR) * HOUR };
}

/** Pełne godziny na osi (podziałka). */
export function hourTicks(range: Interval): number[] {
  const ticks: number[] = [];
  for (let at = range.start; at <= range.end; at += HOUR) ticks.push(at);
  return ticks;
}

export interface Block {
  activity: AgendaActivity;
  interval: Interval;
  /** Podwiersz w torze osoby – nakładające się zajęcia (typy nieblokujące czasu) idą jedne pod drugimi. */
  row: number;
}

/** Zajęcia osoby jako bloki osi, z podwierszami dla nakładających się. */
export function personBlocks(activities: AgendaActivity[]): { blocks: Block[]; rows: number } {
  const sorted = [...activities].sort((x, y) => startOf(x) - startOf(y) || endOf(y) - endOf(x));
  const rowEnds: number[] = [];
  const blocks = sorted.map((activity) => {
    const interval = { start: startOf(activity), end: endOf(activity) };
    let row = rowEnds.findIndex((end) => end <= interval.start);
    if (row === -1) row = rowEnds.length;
    rowEnds[row] = interval.end;
    return { activity, interval, row };
  });
  return { blocks, rows: Math.max(1, rowEnds.length) };
}

/** Wolne okna w zakresie dnia: czas bez zajęć blokujących czas (BR-33), co najmniej `MIN_GAP_MS`. */
export function freeGaps(busy: Interval[], range: Interval): Interval[] {
  const gaps: Interval[] = [];
  let cursor = range.start;
  for (const b of [...busy].sort((x, y) => x.start - y.start)) {
    if (b.start - cursor >= MIN_GAP_MS) gaps.push({ start: cursor, end: Math.min(b.start, range.end) });
    cursor = Math.max(cursor, b.end);
  }
  if (range.end - cursor >= MIN_GAP_MS) gaps.push({ start: cursor, end: range.end });
  return gaps;
}

export interface Suggestions {
  /** Zajęcia z zapisami mieszczące się w oknie – dostępne dla osoby albo już zapisane (po zapisie z okna). */
  enroll: AgendaActivity[];
  /** Wstęp wolny (wykłady, stoiska, pokazy) trwający w tym oknie i pasujący do osoby (BR-32). */
  open: AgendaActivity[];
}

export interface TimelinePerson {
  kind: 'child' | 'adult';
  birthYear: number | null;
}

/** Co osoba może robić w wolnym oknie. Zajęcia z zapisami – według stanu z serwera (wiek, kolizje, miejsca). */
export function suggestionsFor(
  gap: Interval,
  person: TimelinePerson,
  activities: AgendaActivity[],
  items: Map<string, EligibilityItem>,
  editionYear: number,
  now: number,
): Suggestions {
  const byStart = (x: AgendaActivity, y: AgendaActivity) => startOf(x) - startOf(y) || x.title.localeCompare(y.title, 'pl');
  const live = activities.filter((a) => a.status !== 'cancelled');
  const enroll = live
    .filter((a) => a.enrollment && startOf(a) >= gap.start && endOf(a) <= gap.end)
    .filter((a) => {
      const state = items.get(a.id)?.state;
      return state === 'available' || state === 'enrolled';
    })
    .sort(byStart);
  const open = live
    .filter((a) => !a.enrollment && startOf(a) < gap.end && endOf(a) > gap.start && endOf(a) > now)
    .filter((a) => fitsAudience(a, person, editionYear))
    .sort(byStart);
  return { enroll, open };
}

/** Położenie na osi w procentach szerokości. */
export function position(interval: Interval, range: Interval): { left: string; width: string } {
  const span = range.end - range.start;
  return {
    left: `${((interval.start - range.start) / span) * 100}%`,
    width: `${((interval.end - interval.start) / span) * 100}%`,
  };
}
